import { Buffer } from "buffer";
import { ButtonProps } from "@mui/material/Button/Button";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { Formik, FormikProps } from "formik";
import { useCallback, useMemo, useRef, useState } from "react";
import { Dialog } from "../../../components/Dialog";
import { useToast } from "../../../hooks/useToast";
import { useStyles } from "../styles";
import { map } from "lodash";
import { Insights } from "../../../types/insights";
import { Channels } from "../../../components/CreateCampaign/Channels";
import { Message } from "../../../components/CreateCampaign/Message";
import { Infos } from "../../../components/CreateCampaign/Infos";
import { Review } from "../../../components/CreateCampaign/Review";
import { LinkedCampaign } from "../../../components/CreateCampaign/LinkedCampaign";
import {
  CreateCampaignData,
  EmptyProspeccaoData,
} from "../../../types/campaign";
import { addDays, addMinutes, subYears, parseISO } from "date-fns";
import { Automation } from "../../../components/CreateCampaign/Automation";
import { useMutation } from "react-query";
import {
  createCampaign,
  saveEmptyProspeccao,
} from "../../../services/campaigns";
import { useAws } from "../../../hooks/useAws";
import { useAuth } from "../../../hooks/useAuth";
import { FileToBase64 } from "../../../lib/toBase64";
import { CREATE_CAMPAIGN_VALIDATION_SCHEMA } from "./validationCreateCampaign";

type NewCampaignProps = {
  insight: Insights;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  isChild?: boolean;
  parentCampaignName?: string;
  parentCampaignId?: number;
  parentCampaignEndDate?: Date;
};

const stepTranslate = ({ isChild, valueString }) => {
  if (!isChild) {
    switch (valueString) {
      case "channels":
        return 0;
      case "infos":
        return 1;
      case "message":
        return 2;
      case "automation":
        return 3;
      case "review":
        return 4;
      case "last":
        return 4;
      default:
        return null;
    }
  } else {
    switch (valueString) {
      case "linked":
        return 0;
      case "channels":
        return 1;
      case "infos":
        return 2;
      case "message":
        return 3;
      case "automation":
        return 4;
      case "review":
        return 5;
      case "last":
        return 5;
      default:
        return null;
    }
  }
};

export function NewCampaign({
  onClose,
  onSuccess,
  open,
  insight,
  isChild,
  parentCampaignName = "Nome da Campanha",
  parentCampaignId = null,
  parentCampaignEndDate = null,
}: NewCampaignProps) {
  const formikRef = useRef<FormikProps<CreateCampaignData>>(null);

  const { uploadS3 } = useAws();
  const { user } = useAuth();
  const toast = useToast();
  const styles = useStyles();

  const [activeStep, setActiveStep] = useState<number>(0);
  const [loadingSendingCampaign, setLoadingSendingCampaign] =
    useState<boolean>(false);

  const steps = useMemo(() => {
    return [
      ...(isChild ? ["Escolha um público"] : []),
      "Canais",
      "Informações",
      "Mensagem",
      "Automação",
      "Resumo",
    ];
  }, [isChild]);

  const newSaveEmptyProspeccao = useMutation(
    async (data: EmptyProspeccaoData) => {
      const response = await saveEmptyProspeccao(data);
      return response;
    },
    {
      onError: () => {
        toast.show("Erro ao criar a audiência vazia!", { severity: "error" });
      },
    }
  );

  const newCampaign = useMutation(
    async (data: CreateCampaignData) => {
      const response = await createCampaign(data);
      return response;
    },
    {
      onError: () => {
        toast.show("Erro ao criar a campanha!", { severity: "error" });
      },
    }
  );

  const handleClose = useCallback(() => {
    setActiveStep(0);
    onClose();
  }, [onClose, setActiveStep]);

  const handleSave = useCallback(
    async (data: CreateCampaignData) => {
      setLoadingSendingCampaign(true);
      if (data.channel === "whatsapp" && Boolean(data.whatsAppFile)) {
        const base64 = await FileToBase64(data.whatsAppFile);
        const file = base64.split(";base64,")[1];
        const uploadQrCode = {
          file: Buffer.from(file, "base64"),
          type: data.whatsAppFile.type,
          bucketName: "datasalesio-imagens",
          folderName: "whatsappcampanha",
          subFolderName: `${user.companyId}/`,
          distinctName: true,
          extension: data.whatsAppFile.type.split("/")[1],
        };
        const url = await uploadS3(uploadQrCode);
        data = { ...data, whatsAppIsMidia: true, whatsAppUrlMidia: url };
      }
      let insertIfChild = {};
      let idInsightByType = null;
      if (isChild) {
        switch (formikRef.current.values.typeCampaign) {
          case 1:
            idInsightByType = insight.fullListProspecteiType1.idListProspectei;
            break;
          case 2:
            idInsightByType = insight.fullListProspecteiType2.idListProspectei;
            break;
        }
        insertIfChild = {
          idInsight: idInsightByType,
          prospeccao_tipo: formikRef.current.values.typeCampaign,
          origem_campanha: formikRef.current.values.parentCampaignId,
        };
      }
      await newCampaign.mutateAsync({
        ...data,
        ...insertIfChild,
      });

      await newSaveEmptyProspeccao.mutateAsync({
        prospeccao_tipo: 1,
        prospeccao_origem: idInsightByType || insight.idListProspectei,
      });
      await newSaveEmptyProspeccao.mutateAsync({
        prospeccao_tipo: 2,
        prospeccao_origem: idInsightByType || insight.idListProspectei,
        close: true,
      });
      setLoadingSendingCampaign(false);
      handleClose();
      onSuccess();
    },
    [
      uploadS3,
      newCampaign,
      user.companyId,
      isChild,
      newSaveEmptyProspeccao,
      insight?.idListProspectei,
      insight?.fullListProspecteiType1?.idListProspectei,
      insight?.fullListProspecteiType2?.idListProspectei,
      handleClose,
      onSuccess,
    ]
  );

  const handleValidateStep = useCallback((): Promise<boolean> => {
    return new Promise(async (resolve, reject) => {
      try {
        switch (activeStep) {
          case stepTranslate({ isChild, valueString: "linked" }):
            await formikRef.current?.validateField("typeCampaign");
            break;
          case stepTranslate({ isChild, valueString: "channel" }):
            await formikRef.current?.validateField("channel");
            break;
          case stepTranslate({ isChild, valueString: "infos" }):
            await formikRef.current?.validateField("name");
            await formikRef.current?.validateField("startDate");
            await formikRef.current?.validateField("endDate");
            await formikRef.current?.validateField("sendDate");
            await formikRef.current?.validateField("selectedRecipients");
            await formikRef.current?.validateField("whatsAppInstanceStatus");
            await formikRef.current?.validateField("whatsAppInstance");
            break;
          case stepTranslate({ isChild, valueString: "message" }):
            await formikRef.current?.validateField("message");
            await formikRef.current?.validateField("nameSender");
            await formikRef.current?.validateField("emailSender");
            await formikRef.current?.validateField("subject");
            break;
          case stepTranslate({ isChild, valueString: "automation" }):
            await formikRef.current?.validateField("isAutomation");
            await formikRef.current?.validateField("intervalAutomation");
            await formikRef.current?.validateField("intervalFrequency");
            await formikRef.current?.validateField("whenSendAutomation");
            await formikRef.current?.validateField("intervalHour");
            break;
        }

        resolve(formikRef.current?.isValid);
      } catch (err) {
        reject(false);
      }
    });
  }, [activeStep, isChild]);

  const handleGoNext = useCallback(async () => {
    try {
      const isValid = await handleValidateStep();
      if (isValid) {
        setActiveStep((prev) => prev + 1);
      }
    } catch (err) {
      console.log(err);
    }
  }, [handleValidateStep]);

  const handleGoBack = useCallback(() => {
    formikRef.current?.setErrors({});
    setActiveStep((prev) => prev - 1);
  }, []);

  const contentNextButton = useMemo(() => {
    return loadingSendingCampaign
      ? "Enviando solicitação..."
      : activeStep === stepTranslate({ isChild, valueString: "last" })
      ? "Disparar"
      : "Continuar";
  }, [loadingSendingCampaign, activeStep, isChild]);

  const actions: ButtonProps[] = useMemo(() => {
    return [
      {
        children: activeStep === 0 ? "Cancelar" : "Voltar",
        onClick: () => handleGoBack(),
        disabled: activeStep === 0 || loadingSendingCampaign,
      },
      {
        disabled: loadingSendingCampaign,
        children: contentNextButton,
        variant: "contained",
        onClick: () => {
          return activeStep < stepTranslate({ isChild, valueString: "last" })
            ? handleGoNext()
            : activeStep === stepTranslate({ isChild, valueString: "last" }) &&
                formikRef.current?.handleSubmit();
        },
      },
    ];
  }, [
    contentNextButton,
    loadingSendingCampaign,
    activeStep,
    formikRef,
    handleGoBack,
    handleGoNext,
    isChild,
  ]);

  const insightToInfo = useCallback(() => {
    switch (formikRef?.current?.values?.typeCampaign) {
      case 1:
        return insight.fullListProspecteiType1;
      case 2:
        return insight.fullListProspecteiType2;
      default:
        return insight;
    }
  }, [insight]);

  const renderContent = useCallback(() => {
    switch (activeStep) {
      case stepTranslate({ isChild, valueString: "linked" }):
        return <LinkedCampaign />;
      case stepTranslate({ isChild, valueString: "channels" }):
        return <Channels />;
      case stepTranslate({ isChild, valueString: "infos" }):
        return (
          <Infos
            insight={insightToInfo()}
            parentCampaignEndDate={parentCampaignEndDate}
          />
        );
      case stepTranslate({ isChild, valueString: "message" }):
        return <Message />;
      case stepTranslate({ isChild, valueString: "automation" }):
        return <Automation />;
      case stepTranslate({ isChild, valueString: "review" }):
        return <Review />;
      default:
        return <></>;
    }
  }, [activeStep, isChild, insightToInfo, parentCampaignEndDate]);

  return (
    <Dialog
      maxWidth="lg"
      fullWidth
      title="Novo disparo"
      open={open}
      actions={actions}
      onClose={handleClose}
    >
      <Stepper activeStep={activeStep}>
        {map(steps, (step) => {
          return (
            <Step key={step}>
              <StepLabel>{step}</StepLabel>
            </Step>
          );
        })}
      </Stepper>

      <div className={styles.rootContent}>
        <>
          <Formik
            innerRef={formikRef}
            initialValues={
              {
                channel: "sms",
                campaignSendingType: 2.1,
                coastCampaign: 0,
                measureCampaign: 2,
                name: null,
                startDate: new Date(),
                endDate: addDays(new Date(), 7),
                sendDate: addMinutes(new Date(), 10),
                nameInsight: insight?.nameList,
                idInsight: insight?.idListProspectei,
                selectedRecipients: [],
                recipientsCopy: [],
                message: "",
                nameSender: null,
                emailSender: null,
                subject: null,
                whatsAppInstance: null,
                whatsAppFile: null,
                isAutomation: false,
                intervalFrequency: null,
                whenSendAutomation: null,
                intervalHour: new Date(),
                whatsAppInstanceStatus: false,
                isChild,
                parentCampaignName,
                parentCampaignId,
                typeCampaign: null,
                digitalTabloidId: null,
                coupon: null,
                parentCampaignEndDate: parentCampaignEndDate
                  ? // @ts-ignore: Unreachable code error
                    parseISO(parentCampaignEndDate)
                  : subYears(new Date(), 1),
              } as CreateCampaignData
            }
            validationSchema={CREATE_CAMPAIGN_VALIDATION_SCHEMA}
            validateOnChange={false}
            onSubmit={handleSave}
          >
            {renderContent()}
          </Formik>
        </>
      </div>
    </Dialog>
  );
}
